import 'react-perfect-scrollbar/dist/css/styles.css';
import React, { ReactElement, useCallback, useState, useRef, useEffect } from 'react';
import { Menu, Layout, theme, Button, Tooltip, Badge } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import Icon, {
	KeyOutlined,
	FileOutlined,
	HomeOutlined,
	DownloadOutlined,
	ShoppingCartOutlined,
	ImportOutlined,
	YoutubeOutlined,
	DiscordOutlined,
	QuestionCircleOutlined,
	RocketOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CustomWidgetVariant, RecipeType } from '@kemu-io/kemu-types';
import classNames from 'classnames';
import LogoAndText from '../../assets/img/kemu-logo-text.png';
import Logo from '../../assets/img/kemu-logo.png';
import { ReactComponent as RecipeIcon } from '../../assets/img/menus/recipe.svg';
import { ReactComponent as NewRecipeTabIcon } from '../../assets/img/menus/new-recipe-tab.svg';
import { ReactComponent as SaveWidgetIcon } from '../../assets/img/menus/save-widget-icon.svg';
import { ReactComponent as SecretsMappingIcon } from '@src/assets/img/menus/secrets-mapping.svg';
import { ReactComponent as ManageEnvironmentsIcon } from '@src/assets/img/menus/manage-environments.svg';
import { ReactComponent as SaveIcon } from '../../assets/img/menus/save.svg';
import { ReactComponent as SaveAsIcon } from '../../assets/img/menus/save-as.svg';
import { ReactComponent as SnippetsIcon } from '../../assets/img/menus/snippets.svg';
import { selectCurrentRecipePoolId, selectCurrentRecipe, selectedBlock, selectVisibleGroup } from '../../features/Workspace/workspaceSlice';
import { showMissingServicesModal, showSecretMappingModal, showHubApiKeyModal, showDeployRecipeModal, showManageEnvironmentsModal, selectColorScheme } from '@src/features/interface/interfaceSlice';
import { getStoredConfig, setStoredConfig, isDevEnvironment } from '../../common/utils';
import globals from '../../common/globals';
import routes from '../../common/routes/index';
import ExportRecipeDialog from '../ExportRecipeDialog/ExportRecipeDialog';
import styles from './mainMenu.module.css';
import AnimatedUpdateIcon from './AnimatedUpdateIcon/AnimatedUpdateIcon';
import useFileHandle from '@hooks/useFileHandle.ts';
import useTranslation from '@hooks/useTranslation';
import { selectCurrentWidget } from '@src/app/reducers/widget/widgetSlice';
import { saveWidgetAction } from '@src/app/reducers/widget/saveWidgetReducer';
import { updateWidgetAction } from '@src/app/reducers/widget/updateWidgetReducer';
import { unlinkCustomWidgetAction } from '@src/features/LogicMapper/logicMapperSlice';
import { storeRecipeAction } from '@src/features/interface/reducers/storeRecipeReducer';
import UserAccountButton from '@components/AccountMenu/UserAccountButton';
import SaveRecipeTypeModal from '@components/SaveRecipeTypeModal/SaveRecipeTypeModal';
import SaveWidgetTypeModal from '@components/SaveWidgetTypeModal/SaveWidgetTypeModal';
import { saveRecipeToFileAction } from '@src/features/interface/reducers/saveRecipeToFileReducer';
import { importFileRecipeAction } from '@src/app/reducers/workspace/importFileRecipeReducer';
import useHubUpdateDetector from '@common/hooks/useHubUpdateDetector';
import useAlert from '@components/alert/useAlert';
import { selectSessionProperties, setSessionProperty } from '@src/app/reducers/user/userSlice';
import useAnalyticsTracker from '@common/hooks/useAnalyticsTracker';
import historyManager from '@src/app/reducers/history/historyManager';
import useSubscriptionInfo, { BaseSubscriptionInfo } from '@common/hooks/useSubscriptionInfo';
import { getTierColor } from '@components/helpers';
import useWidgetState from '@common/hooks/useWidgetState';
import useMissingHubServices from '@hooks/useMissingHubServices';
import useRecipeHasRequiredSecrets from '@src/hooks/useRecipeHasRequiredSecrets';
import { toggleSnippetsDrawer } from '@src/app/reducers/snippets/snippetsSlice';
import useUpdateRecipeFile from '@hooks/useUpdateRecipeFile';
import { WidgetType } from '@kemu-io/kemu-core/types';
import useHubSystemInfo from '@common/hooks/useHubSystemInfo';


const { Sider } = Layout;
interface Props {
	collapsed: boolean;
	onShowHelp?: (config: any) => void;
}

interface MenuConfig {
	openedMenus: string[];
}

enum MarketplaceTarget {
	PublicRecipes,
	MyRecipes,
	HubServices,
}

const CONFIG_NAME = 'selectedMenus';
const defaultConfig: MenuConfig = {
	openedMenus: ['apps']
};

export const getRecipeContents = async (recipeId: string): Promise<{
  recipe: any;
  storage?: ArrayBuffer;
}> => {
  // TODO: Implement actual recipe content retrieval
  return {
    recipe: {
      id: recipeId,
      name: 'Dummy Recipe',
      blocks: {},
      connections: [],
      version: '1.0.0',
    },
    storage: new ArrayBuffer(0),
  };
};

/**
 * Returns a pastel color based on the subscription tier.
 * Free: light blue, Starter: light green, Teams: light purple, Enterprise: light orange, Custom: gold
 * @param tier - The subscription tier information
 * @returns The color code for the tier
 */
const colorByTier = (tier?: BaseSubscriptionInfo | null): string | undefined  => {
	const tierName = tier?.lookupKey.toLowerCase().split('_')[0];
	if (tier?.isCustomTier) { return undefined; }

	switch (tierName) {
		case 'free':
			return getTierColor(1);
		case 'starter':
			return getTierColor(2);
		case 'teams':
			return getTierColor(3);
		case 'business':
			return getTierColor(4);
		case 'enterprise':
		default:
			return undefined; // Default (Kemu purple)
	}
};

function MainMenu ({ collapsed, onShowHelp }: Props): ReactElement {
	const dispatch = useDispatch();
	const Alert = useAlert();
	// const hbList = useSelector(hardwareBlocks);
	const [openedItems, setOpenedItems] = useState<MenuConfig>(getStoredConfig(CONFIG_NAME, defaultConfig));
	const { token: { colorBgContainer } } = theme.useToken();
	const colorScheme = useSelector(selectColorScheme);
	const { trackEvent } = useAnalyticsTracker('main_menu');
	const navigate = useNavigate();
	const t = useTranslation('Menu');
	const sessionProperties = useSelector(selectSessionProperties);
	const visibleGroup = useSelector(selectVisibleGroup);
	const currentRecipe = useSelector(selectCurrentRecipe);
	const currentBlock = useSelector(selectedBlock);
	const { saveCurrentRecipeToDisk } = useUpdateRecipeFile();
	// const showRedDot = useSelector(unsavedChanges);
	const recipePoolId = useSelector(selectCurrentRecipePoolId);
	const currentWidget = useSelector(selectCurrentWidget);
	const [showExportDialog, setShowExportDialog] = useState(false);
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [showSaveWidgetModal, setShowSaveWidgetModal] = useState(false);
	const subscriptionInfo = useSubscriptionInfo();
	const saveAsRef = useRef(false);
	const { fileHandle, setFileHandle } = useFileHandle();
	const { updateInfo, rebootToInstall } = useHubUpdateDetector();
	const missingHubServices = useMissingHubServices();
	const hasRequiredSecrets = useRecipeHasRequiredSecrets();
	const lastShowSecretMappingHighlight = useRef(false);
	const [showSecretMappingHighlight, setShowSecretMappingHighlight] = useState(false);
	const systemInfo = useHubSystemInfo();
	const hasValidApiKey = systemInfo?.hasValidApiKeyConfigured ?? true;
	
	const handleShowHubApiKeyModal = useCallback(() => {
		dispatch(showHubApiKeyModal(true));
	}, [dispatch]);

	// Effect to highlight secret mapping when it first appears
	useEffect(() => {
		const wasOff = !lastShowSecretMappingHighlight.current;
		const isNowOn = hasRequiredSecrets;
		lastShowSecretMappingHighlight.current = hasRequiredSecrets;
		
		if (wasOff && isNowOn) {
			setShowSecretMappingHighlight(true);
		}
	}, [hasRequiredSecrets]);

	// Get widget state using reactive hook
	const { getState } = useWidgetState(
		recipePoolId || '',
		currentBlock?.recipeId || '',
		visibleGroup?.groupId || ''
	);
	const openMarketplace = useCallback((target: MarketplaceTarget) => {
		if (target === MarketplaceTarget.PublicRecipes) { navigate(routes.marketplace.getPublicationListRoute('recipe')); };
		if (target === MarketplaceTarget.MyRecipes) { navigate(routes.marketplace.getMyRecipesRoute()); };
		if (target === MarketplaceTarget.HubServices) { navigate(routes.marketplace.getHubServicesRoute()); };
	}, [navigate]);

	const createNewRecipe = useCallback(() => {
		window.open(`https://${globals.KEMU_APP_DOMAIN}`, '_blank');
	}, []);

	const createNewRecipeHere = useCallback(() => {
		window.open(`https://${globals.KEMU_APP_DOMAIN}`, '_self');
	}, []);

	const handleExportRecipe = useCallback(() => {
		setShowExportDialog(true);
	}, []);

	const handleExportDialogClose = useCallback(() => {
		setShowExportDialog(false);
	}, []);

	// Helper methods for widget operations
	const saveWidgetAsNew = useCallback(() => {
		if (currentBlock?.recipeId && visibleGroup?.groupId && recipePoolId) {
			dispatch(saveWidgetAction({
				recipeId: recipePoolId,
				thingId: currentBlock.recipeId,
				widgetIdInRecipe: visibleGroup.groupId,
				variant: CustomWidgetVariant.Group,
			}));
		}
	}, [dispatch, visibleGroup, currentBlock, recipePoolId]);

	const updateExistingWidget = useCallback(() => {
		if (currentBlock?.recipeId && visibleGroup?.groupId && recipePoolId && currentWidget) {
			dispatch(updateWidgetAction({
				widgetDbId: currentWidget,
				recipeId: recipePoolId,
				thingId: currentBlock.recipeId,
				widgetRecipeId: visibleGroup.groupId,
				variant: CustomWidgetVariant.Group,
			}));
		}
	}, [dispatch, visibleGroup, currentBlock, recipePoolId, currentWidget]);

	// Detect if the widget already exists, and if so, call update instead
	const saveWidgetHandler = useCallback(() => {
		if (currentBlock?.recipeId && visibleGroup?.groupId && recipePoolId) {
			// Check if the widget has collectionInfo (is linked to original)
			const widgetState = getState(visibleGroup.groupId);
			const widgetIsLinkedToOriginal = widgetState?.collectionInfo !== undefined;

			if (widgetIsLinkedToOriginal) {
				// Show modal to choose between creating new or updating original
				setShowSaveWidgetModal(true);
			} else {
				// No collectionInfo, proceed with normal save logic
				if (!currentWidget) {
					saveWidgetAsNew();
				} else {
					updateExistingWidget();
				}
			}
		}
	}, [visibleGroup, currentWidget, currentBlock, recipePoolId, saveWidgetAsNew, updateExistingWidget, setShowSaveWidgetModal, getState]);

	const handleSaveWidgetModalClose = useCallback(() => {
		setShowSaveWidgetModal(false);
	}, []);

	const handleUnlinkAndCreateNewWidget = useCallback(() => {
		if (currentBlock?.recipeId && visibleGroup?.groupId && recipePoolId) {
			// First unlink the widget
			dispatch(unlinkCustomWidgetAction({
				recipeId: recipePoolId,
				thingId: currentBlock.recipeId,
				widgetId: visibleGroup.groupId,
			}));

			// Then save as new widget
			saveWidgetAsNew();
		}
	}, [dispatch, visibleGroup, currentBlock, recipePoolId, saveWidgetAsNew]);

	const saveToCloud = useCallback(() => {
    // FIXME: restore showRedDot
    // if (/*showRedDot && */ recipePoolId) {
    if (recipePoolId) {
			trackEvent('save_to_cloud_button_click');
      dispatch(
        storeRecipeAction({
          recipePoolId: recipePoolId,
          navigate,
          saveAsNew: saveAsRef.current,
        })
      );

      saveAsRef.current = false;
    }
  }, [dispatch, recipePoolId, navigate, trackEvent]);

	const handleConfirmBeforeUpdate = useCallback(() => {
		trackEvent('hub_update_button_click');
		Alert.confirm({
			title: t('Update.Confirm'),
			iconColor: 'info',
			content: t('Update.Confirm.Content'),
			okText: t.withBaseKey('CommonWords')('Update'),
			// okButtonColor: 'danger',
			cancelText: t.withBaseKey('CommonWords')('Cancel'),
			onOk: () => {
				trackEvent('hub_update_button_confirm');
				rebootToInstall();
			}
		});
	}, [t, rebootToInstall, Alert, trackEvent]);

	const handleOpenOnboarding = useCallback(() => {
		trackEvent('onboarding_button_click');
		dispatch(setSessionProperty({
			onboardingModalVisible: true
		}));
	}, [dispatch, trackEvent]);

	const handleOpenDiscord = useCallback(() => {
		trackEvent('discord_button_click');
		window.open(`https://discord.gg/${t('Discord.InviteCode')}`, '_blank');
	}, [trackEvent, t]);

	const handleOpenPlatformHelp = useCallback(() => {
		trackEvent('platform_help_button_click');
		// Show ServiceHelpPanel for platform docs
		const platformHelpConfig = {
			widgetId: 'platform-help',
			type: WidgetType.hubService,
			recipeId: 'platform',
			thingRecipeId: 'platform',
			customTitle: 'Platform Help'
		};
		onShowHelp?.(platformHelpConfig);
	}, [trackEvent, onShowHelp]);

	const handleOnboardingTooltipOpenChange = useCallback((open: boolean) => {
		if (!open) {
			dispatch(setSessionProperty({ videoTooltipShown: true }));
		}
	}, [dispatch]);

	const handleShowMissingServices = useCallback(() => {
		dispatch(showMissingServicesModal(true));
	}, [dispatch]);

	const handleShowSecretMapping = useCallback(() => {
		dispatch(showSecretMappingModal(true));
		setShowSecretMappingHighlight(false);
	}, [dispatch]);

	const handleShowDeployRecipe = useCallback(() => {
		dispatch(showDeployRecipeModal(true));
	}, [dispatch]);

	const handleShowManageEnvironments = useCallback(() => {
		dispatch(showManageEnvironmentsModal(true));
	}, [dispatch]);

	const handleOpenSnippets = useCallback(() => {
		dispatch(toggleSnippetsDrawer());
	}, [dispatch]);


	const saveToFile = useCallback(async () => {
		if (!recipePoolId) {
			return;
		}

		if(currentRecipe.loadedFromDisk){
			saveCurrentRecipeToDisk();
			return;
		}

		trackEvent('save_to_file_button_click');
		await dispatch(saveRecipeToFileAction({
			navigate,
			handle: fileHandle,
			saveHandle: setFileHandle,
		}));
	}, [recipePoolId, navigate, dispatch, fileHandle, setFileHandle, trackEvent, saveCurrentRecipeToDisk, currentRecipe.loadedFromDisk]);

	const handleSaveClick = useCallback(() => {
		if (currentRecipe.type === RecipeType.Desktop) {
			saveToFile();
		} else if (currentRecipe.type === RecipeType.Browser && currentRecipe.entityInfo?.dbId) {
			saveToCloud();
		} else {
			setShowSaveModal(true);
		}
	}, [currentRecipe.type, currentRecipe.entityInfo?.dbId, saveToFile, saveToCloud]);

	const handleSaveAs = useCallback(() => {
		// Reset handler to force users to choose a target file
		setFileHandle(null);
		saveAsRef.current = true;
		setShowSaveModal(true);
	}, [setFileHandle]);

	const handleSaveModalClose = useCallback(() => {
		setShowSaveModal(false);
		saveAsRef.current = false;
	}, []);

	const onOpenChange = (keys: React.ReactText[]) => {
		// when the menu collapses, keys is an empty array and it would delete everything 
		if (!collapsed) {
			setOpenedItems({
				openedMenus: keys as string[]
			});

			setStoredConfig<MenuConfig>(CONFIG_NAME, {
				openedMenus: keys as string[]
			} as MenuConfig, true);
		}
	};

	const handleImportRecipe = useCallback(() => {

		dispatch(importFileRecipeAction({
			navigate,
			// IMPORTANT: we don't pass the current handle to force the user to pick a new file
			saveHandle: setFileHandle,
		}));
	}, [dispatch, navigate, setFileHandle]);

	const inDevEnvironment = isDevEnvironment();
  const histState = historyManager.getState();
  const canUndo = histState.canUndo;
  const canRedo = histState.canRedo;

	const firstTimeOnboardingTooltip = !sessionProperties.videoTooltipShown && !sessionProperties.onboardingModalVisible;
	return (
		<Sider
			theme={colorScheme}
			style={{ background: colorBgContainer }}
			width={250}
			className={styles.MenuSider}
			trigger={null}
			collapsible
			collapsed={collapsed}
		>
			<div className={styles.SidebarContainer}>
				<div>
					{/* Logo */}
					<div className={styles.SidebarBrand}>
						<a href="/">
							{!collapsed ? (
								<img alt="image" src={LogoAndText} className={styles.HeaderLogo} />
							) : (
								<img alt="image" src={Logo} className={styles.CollapsedHeaderLogo} />
							)}
						</a>
					</div>

					{/* Menu */}
					<Menu
						theme={colorScheme}
						onOpenChange={onOpenChange}
						triggerSubMenuAction="hover"
						defaultOpenKeys={!collapsed ? openedItems.openedMenus: []}
						mode="inline"
						selectable={false}
						subMenuCloseDelay={0.25}
						className={classNames(styles.Menu, collapsed && styles.Collapsed)}
						items={[
							{
								key: 'home',
								icon: <HomeOutlined style={{ fontSize: 26 }} />,
								title: collapsed ? t('Home') : '',
								label: t('Home'),
								className: styles.LargeMenu,

								children: [
									{
										key: 'new-recipe-here',
										icon: <FileOutlined />,
										label: t('NewRecipeHere'),
										onClick: createNewRecipeHere,
									},
									{
										key: 'new-recipe',
										icon: <Icon component={NewRecipeTabIcon} />,
										label: t('NewRecipeTab'),
										onClick: createNewRecipe,
									},
									{
										key: 'my-recipes-item',
										icon: <Icon component={RecipeIcon} />,
										label: t('MyRecipes'),
										onClick: () => openMarketplace(MarketplaceTarget.MyRecipes),
									},
									{
										key: 'import-recipe',
										icon: <ImportOutlined />,
										label: t('ImportRecipe'),
										onClick: handleImportRecipe,
									},
									{
										key: 'export-recipe',
										icon: <DownloadOutlined />,
										label: t('ExportRecipe'),
										onClick: handleExportRecipe,
									},
									// {
									// 	key: 'marketplace-item',
									// 	icon: <ShoppingCartOutlined />,
									// 	label: t('Marketplace'),
									// 	onClick: () => openMarketplace(MarketplaceTarget.PublicRecipes),
									// },
								]
							},
							{
								key: 'save',
								className: styles.LargeMenu,
								icon: <Icon component={SaveIcon} style={{ fontSize: 26 }} />,
								title: collapsed ? t('SaveRecipeTooltip') : '',
								label: t('SaveRecipeTooltip'),
								children: [
									{
										key: 'save-recipe',
										icon: <Icon component={SaveIcon} />,
										label: t('SaveRecipe'),
										onClick: handleSaveClick,
									},
									{
										key: 'save-as',
										icon: <Icon component={SaveAsIcon} style={{ fontSize: 16 }} />,
										label: t('SaveRecipeAs'),
										onClick: handleSaveAs,
									},
								],
							},
							...(visibleGroup ? [
								{
									key: 'save-widget',
									className: styles.LargeMenu,
									icon: <Icon component={SaveWidgetIcon} style={{ fontSize: 26 }} />,
									title: collapsed ? t('SaveWidgetTooltip') : '',
									label: t('SaveWidgetTooltip'),
									onClick: saveWidgetHandler,
								},
							]: []),
							/* {
								key: 'history',
								className: styles.LargeMenu,
								icon: <UndoOutlined style={{ fontSize: 26 }} />,
								title: collapsed ? t('History') : '',
								label: t('History'),
								children: [
									{
										key: 'undo',
										icon: <UndoOutlined />,
										label: t('History.Undo'),
										disabled: !canUndo,
										onClick: () => dispatch(undoHistoryAction()),
									},
									{
										key: 'redo',
										icon: <RedoOutlined />,
										label: t('History.Redo'),
										disabled: !canRedo,
										onClick: () => dispatch(redoHistoryAction()),
									},
									{
										key: 'clear',
										icon: <RedoOutlined />,
										label: t('History.Clear'),
										disabled: !recipePoolId,
										onClick: () => historyManager.clear(recipePoolId as string),
									},
								],
							}, */
							{
								key: 'marketplace',
								className: styles.LargeMenu,
								label: t('Marketplace'),
								icon: <ShoppingCartOutlined style={{ fontSize: 26 }} />,
									// icon: (
								// 	<Icon
								// 		component={visibleGroup ? SaveWidgetIcon : SaveRecipeIcon}
								// 		style={{ fontSize: 26 }}
								// 	/>
								// ),
								onClick: () => openMarketplace(MarketplaceTarget.HubServices),
							},
							{
								key: 'snippets',
								className: styles.LargeMenu,
								label: t('Snippets'),
								icon: <Icon component={SnippetsIcon} style={{ fontSize: 26 }} />,
								onClick: handleOpenSnippets,
							},
							...(hasRequiredSecrets ? [
								{
									key: 'secret-mapping',
									className: classNames(
										styles.LargeMenu, 
										styles.CustomMenu,
										showSecretMappingHighlight && styles.DownloadServiceButton
									),
									label: t('SecretMapping'),
									icon: <Icon component={SecretsMappingIcon} style={{ '--fontSize': '36px' } as React.CSSProperties} />,
									title: collapsed ? t('SecretMappingTooltip') : '',
									onClick: handleShowSecretMapping,
								},
							]: []),
							{
								key: 'manage-environments',
								className: styles.LargeMenu,
								label: t('ManageEnvironments'),
								icon: <Icon component={ManageEnvironmentsIcon} style={{ fontSize: 26 }} />,
								title: collapsed ? t('ManageEnvironmentsTooltip') : '',
								onClick: handleShowManageEnvironments,
							},
							...(currentRecipe?.entityInfo?.dbId ? [
								{
									key: 'deploy-recipe',
									className: styles.LargeMenu,
									label: t('DeployRecipe'),
									icon: <RocketOutlined style={{ fontSize: 26 }} />,
									title: collapsed ? t('DeployRecipeTooltip') : '',
									onClick: handleShowDeployRecipe,
								},
							]: []),
							...(missingHubServices.length > 0 ? [
								{
									key: 'missing-services',
									className: classNames(styles.LargeMenu, styles.DownloadServiceButton),
									label: t('MissingServices'),
									icon: <DownloadOutlined style={{ fontSize: 26 }} />,
									onClick: handleShowMissingServices,
								}
							]: []),
							...(!hasValidApiKey ? [
								{
									key: 'configure-api-key',
									className: styles.LargeMenu,
									label: t('ConfigureApiKey'),
									icon: <KeyOutlined style={{ fontSize: 26 }} />,
									title: collapsed ? t('ConfigureApiKeyTooltip') : '',
									onClick: handleShowHubApiKeyModal,
								}
							]: []),
						]}
					/>
				</div>

				<div className={styles.PushDownItems}>
					
					{updateInfo && (
						<div className={styles.HubUpdateButton}>
							<Tooltip title={t('Update', undefined, { version: updateInfo.version })} placement='right'>
								<Button type="text" icon={<AnimatedUpdateIcon />} onClick={handleConfirmBeforeUpdate} />
							</Tooltip>
						</div>
					)}

					{/* Platform Help Button */}
					<div className={styles.OnboardingButton}>
						<Tooltip
							title="Platform Help"
							placement='right'
						>
							<Button
								type="text"
								icon={<QuestionCircleOutlined style={{ fontSize: 26 }}/>}
								onClick={handleOpenPlatformHelp}
							/>
						</Tooltip>
					</div>

					{/* Onboarding Button */}
					<div className={styles.OnboardingButton}>
						<Tooltip
							title={firstTimeOnboardingTooltip ? t('Onboarding.Button.TooltipFirstTime') : t('Onboarding.Button.Tooltip')}
							placement='right'
							onOpenChange={handleOnboardingTooltipOpenChange}
							open={firstTimeOnboardingTooltip ? true : undefined}
							// defaultOpen={firstTimeOnboardingTooltip}
						>
							<Button
								type="text"
								// icon={<Icon component={YoutubeOutlined} style={{ fontSize: 26 }}/>} 
								icon={<YoutubeOutlined style={{ fontSize: 26 }}/>}
								onClick={handleOpenOnboarding}
							/>
						</Tooltip>
					</div>

					{/* Discord Button */}
					<div className={styles.OnboardingButton}>
						<Tooltip
							title={t('Discord.Button.Tooltip')}
							placement='right'
						>
							<Button
								type="text"
								icon={<DiscordOutlined style={{ fontSize: 26 }}/>}
								onClick={handleOpenDiscord}
							/>
						</Tooltip>
					</div>

					{/* User Menu */}
					<Badge.Ribbon 
						text={subscriptionInfo?.tierName?.replace('Kemu', '').replace('Internal', '')} 
						className={classNames(styles.TierBadge, styles.PremiumBadge)} 
						color={colorByTier(subscriptionInfo)}
					>
						<UserAccountButton className={styles.ProfileMenu} />
					</Badge.Ribbon>

					<span
						className={classNames(styles.VersionBox, inDevEnvironment && styles.Staging)}
					>
						v{globals.WEB_APP_VERSION}
					</span>
					
				</div>

				<ExportRecipeDialog
					visible={showExportDialog}
					onClose={handleExportDialogClose}
				/>

				<SaveRecipeTypeModal
					visible={showSaveModal}
					onClose={handleSaveModalClose}
					onSaveToCloud={saveToCloud}
					onSaveToFile={saveToFile}
				/>

				<SaveWidgetTypeModal
					visible={showSaveWidgetModal}
					onClose={handleSaveWidgetModalClose}
					onCreateNew={handleUnlinkAndCreateNewWidget}
					onUpdateOriginal={updateExistingWidget}
				/>
			</div>
		</Sider>
	);
}

export default MainMenu;
